/* eslint-disable react-refresh/only-export-components */
// ===========================================
// ADMIN ROUTES CONFIGURATION
// ===========================================

import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import { AdminGuard } from './guards/AdminGuard';
import { DomainGuard } from './guards/DomainGuard';
import { ToastProvider } from '@/components/admin/ui';

// Lazy load admin layout
const AdminLayout = lazy(() => import('@/layouts/admin/AdminLayout'));

// Lazy load admin pages
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage'));
const AdminNotFoundPage = lazy(() => import('@/pages/admin/AdminNotFoundPage'));

// Lazy load admin feature pages
const DashboardPage = lazy(() => import('@/features/admin/dashboard/DashboardPage'));
const AttributeManagementPage = lazy(() => import('@/features/admin/settings/AttributeManagementPage'));
const PackagesPage = lazy(() => import('@/features/admin/packages/PackagesPage'));
const DestinationsPage = lazy(() => import('@/features/admin/destinations/DestinationsPage'));
const ToursPage = lazy(() => import('@/features/admin/tours/ToursPage'));
const BookingsPage = lazy(() => import('@/features/admin/bookings/BookingsPage'));
const UsersPage = lazy(() => import('@/features/admin/users/UsersPage'));
const CmsPage = lazy(() => import('@/features/admin/cms/CmsPage'));
const BlogPage = lazy(() => import('@/features/admin/blog/BlogPage'));
const HeroSlidesPage = lazy(() => import('@/features/admin/hero-slides/HeroSlidesPage'));
const WhyChooseUsPage = lazy(() => import('@/features/admin/why-choose-us/WhyChooseUsPage'));
const MediaPage = lazy(() => import('@/features/admin/media/MediaPage'));
const ReviewsPage = lazy(() => import('@/features/admin/reviews/ReviewsPage'));
const SupportPage = lazy(() => import('@/features/admin/support/SupportPage'));

const LanguageManagerPage = lazy(() => import('@/features/admin/settings/LanguageManagerPage'));
const CurrencyManagerPage = lazy(() => import('@/features/admin/settings/CurrencyManagerPage'));
const SettingsPage = lazy(() => import('@/features/admin/settings/SettingsPage'));
const ContactInfoPage = lazy(() => import('@/features/admin/contact-info/ContactInfoPage'));
const PackageFiltersPage = lazy(() => import('@/features/admin/package-filters/PackageFiltersPage'))
const CustomersPage = lazy(() => import('@/features/admin/customers/CustomersPage'))
const MailerPage = lazy(() => import('@/features/admin/mailer/MailerPage'));
const CommentsPage = lazy(() => import('@/features/admin/comments/CommentsPage').then(module => ({ default: module.CommentsPage })));
const PaymentMethodsPage = lazy(() => import('@/features/admin/payment-methods/PaymentMethodsPage'));

// Page loader component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-500"></div>
  </div>
);

// Suspense wrapper
const SuspenseWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/**
 * Admin routes configuration
 * These routes are only loaded when accessing admin.* subdomain
 */
export const adminRoutes: RouteObject[] = [
  // Admin login (only accessible on admin domain)
  {
    path: '/admin/login',
    element: (
      <DomainGuard requireAdmin={true}>
        <ToastProvider>
          <SuspenseWrapper>
            <AdminLoginPage />
          </SuspenseWrapper>
        </ToastProvider>
      </DomainGuard>
    ),
  },

  // Protected admin routes (only accessible on admin domain)
  {
    path: '/admin',
    element: (
      <DomainGuard requireAdmin={true}>
        <AdminGuard>
          <ToastProvider>
            <SuspenseWrapper>
              <AdminLayout />
            </SuspenseWrapper>
          </ToastProvider>
        </AdminGuard>
      </DomainGuard>
    ),
    children: [
      // Dashboard
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },

      // Package Management
      {
        path: 'packages',
        element: (
          <SuspenseWrapper>
            <PackagesPage />
          </SuspenseWrapper>
        ),
      },

      // Destinations Management
      {
        path: 'destinations',
        element: (
          <SuspenseWrapper>
            <DestinationsPage />
          </SuspenseWrapper>
        ),
      },

      // Tours Management
      {
        path: 'tours',
        element: (
          <SuspenseWrapper>
            <ToursPage />
          </SuspenseWrapper>
        ),
      },

      // Bookings Management
      {
        path: 'bookings',
        element: (
          <SuspenseWrapper>
            <BookingsPage />
          </SuspenseWrapper>
        ),
      },

      // Payment Methods Management
      {
        path: 'payment-methods',
        element: (
          <SuspenseWrapper>
            <PaymentMethodsPage />
          </SuspenseWrapper>
        ),
      },

      // Customers Management
      {
        path: 'customers',
        element: (
          <SuspenseWrapper>
            <CustomersPage />
          </SuspenseWrapper>
        ),
      },

      // Email Manager
      {
        path: 'mailer',
        element: (
          <SuspenseWrapper>
            <MailerPage />
          </SuspenseWrapper>
        ),
      },

      // Comments Management
      {
        path: 'comments',
        element: (
          <SuspenseWrapper>
            <CommentsPage />
          </SuspenseWrapper>
        ),
      },

      // Users Management
      {
        path: 'users',
        element: (
          <SuspenseWrapper>
            <UsersPage />
          </SuspenseWrapper>
        ),
      },

      // CMS
      {
        path: 'cms',
        element: (
          <SuspenseWrapper>
            <CmsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'blog',
        element: (
          <SuspenseWrapper>
            <BlogPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'cms/hero-slides',
        element: (
          <SuspenseWrapper>
            <HeroSlidesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'cms/why-choose-us',
        element: (
          <SuspenseWrapper>
            <WhyChooseUsPage />
          </SuspenseWrapper>
        ),
      },

      // Media Library
      {
        path: 'media',
        element: (
          <SuspenseWrapper>
            <MediaPage />
          </SuspenseWrapper>
        ),
      },

      // Reviews
      {
        path: 'reviews',
        element: (
          <SuspenseWrapper>
            <ReviewsPage />
          </SuspenseWrapper>
        ),
      },

      // Support
      {
        path: 'support',
        element: (
          <SuspenseWrapper>
            <SupportPage />
          </SuspenseWrapper>
        ),
      },

      // Translations Editor


      // Settings
      {
        path: 'settings',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'attributes',
        element: (
          <SuspenseWrapper>
            <AttributeManagementPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'contact-info',
        element: (
          <SuspenseWrapper>
            <ContactInfoPage />
          </SuspenseWrapper>
        ),
      },

      {
        path: 'languages',
        element: (
          <SuspenseWrapper>
            <LanguageManagerPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'currencies',
        element: (
          <SuspenseWrapper>
            <CurrencyManagerPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'package-filters',
        element: (
          <SuspenseWrapper>
            <PackageFiltersPage />
          </SuspenseWrapper>
        ),
      },

      // 404 for admin routes
      {
        path: '*',
        element: (
          <SuspenseWrapper>
            <AdminNotFoundPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
];
